import { ColDef, ColGroupDef, CsvExportParams, ExcelExportParams } from "ag-grid-enterprise";
import { CustomCellRendererProps } from "ag-grid-react";
import { ZIcon } from "components/ZIcon";
import { ZIconType } from "helpers/utility/iconMap";
import { ManagedStockOptimizedView } from "models/dbModels/managedStock/ManagedStock";
import { Link } from "react-router-dom";

export function getManagedStockViewConfig(user: any, departments: any, suppliers: any): (ColDef<ManagedStockOptimizedView> | ColGroupDef<ManagedStockOptimizedView>)[] | null {
        
    return [
        {
            headerCheckboxSelection: true,
            checkboxSelection: true,
            width: 45,
            sortable: false,
            filter: false,
            suppressHeaderMenuButton: true,
        },
        {
            headerName: "Name",
            field: "name",
            flex: 3,
            initialSort:"asc",
            minWidth: 250,
            filter: "agTextColumnFilter",
            cellClass: "aggrid-header-link-lext cursor-pointer",
            cellRenderer: (params: CustomCellRendererProps) => {
                return user.permissions.managedStock.canUpdate ? <Link to={`/app/stock/${params.data.id}`}><ZIcon icon={ZIconType.System} size="14px" iconClassName={`me-1 ${params.data?.productId != null ? "" 
                    : "invisible"}`}/>{params.value}</Link> : <><ZIcon icon={ZIconType.System} size="14px" iconClassName={`me-1 ${params.data?.productId != null ? "" : "invisible"}`}/> {params.value}</>;
            }},
        {
            headerName: "Department",
            flex: 2,
            minWidth: 140,
            field: "departmentName"
        },

        {
            headerName: "Preferred Supplier", 
            minWidth: 180,           
            flex: 2,
            field: "supplierName"           
        },
        {
            headerName: "PDE",
            minWidth: 145,
            flex: 2,
            field: "pde",           
            cellClass: 'numberAsString'
        },
        {
            headerName: "APN",
            minWidth: 145,
            flex: 2,
            field: "apn",          
            cellClass: 'numberAsString'
        },
        {
            headerName: "Cost Exc",
            headerTooltip:"Cost Exc",
            minWidth: 120,
            flex: 1,
            field: "costEx",
            cellClass: 'moneyField',
            cellRendererSelector: (_) => {
                return { component: "priceCellRenderer" }
            }          
        },
        {
            headerName: "Sell Price",
            headerTooltip:"Sell Price",
            minWidth: 120,
            flex: 1,
            field: "baseSellPrice",
            cellClass: 'moneyField',
            cellRendererSelector: (_) => {
                return { component: "priceCellRenderer" }
            }          
        },
        {
            headerName: "GP",
            minWidth: 120,
            flex: 1,
            sortable: false,
            field: "gstCode",     
            cellClass: 'numberAsString',
            cellRendererSelector: (_) => {
                return { component: "percentCellRenderer" }
            },
            valueGetter: (params) => getGpPercentValues(params.data)
        },
        {
            headerName: "Last Modified",
            flex: 2,
            minWidth: 190, 
            field: "lastModified",
            cellClass: 'dateField',
            valueGetter: (params) => params.data? new Date(params.data?.lastModified).toLocaleString('en-au',{ hour12: true })
                .replace(",","").toUpperCase(): null
        }
    ];
}

function getGpPercentValues(data: ManagedStockOptimizedView | undefined){
    if(data == undefined){
        return null;
    }
    if(data?.baseSellPrice == undefined || data?.baseSellPrice == null || data?.baseSellPrice < 1){
        return null;
    }
    if(data?.costEx == undefined || data?.costEx == null || data?.costEx < 1){
        return null;
    }
    if(data?.gstCode == undefined || data?.gstCode == null){
        return null;
    }

    return  Math.round(((Number(data?.baseSellPrice) / (data?.gstCode === "Y" ? 1.1 : 1)- data?.costEx) / (Number(data?.baseSellPrice)) / (data?.gstCode === "Y" ? 1.1 : 1) * 100) * 100) / 100;
}

export function getManagerStockCsvDataFormatter(): CsvExportParams{
    return {
        fileName: "ManagedStock_" + new Date().toLocaleDateString('en-AU', {
            day : 'numeric',
            month : 'short',
            year : 'numeric',
            hour : '2-digit',
            minute : '2-digit'
        }).split(' ').join('-').replace(',', '') +".csv",
        columnKeys: [ "name","departmentName","supplierName","pde","apn","costEx","baseSellPrice","gstCode","lastModified" ],
        processCellCallback: (cell) => {
            if (cell.column.getColId() === 'gstCode' && cell.value != null) {             
                return `${cell.value}` + "%";
              }
              if (cell.column.getColId() === 'lastModified' && cell.value != null) {
                return (cell.value as String).split(' ')[0];
              }
              return cell.value;
        },
    }
}
    


export function getManagerStockDataFormatter(): ExcelExportParams {
    return {
        columnKeys: [ "name","departmentName","supplierName","pde","apn","costEx","baseSellPrice","gstCode","lastModified" ],
        processCellCallback: (cell) => {
            if (cell.column.getColId() === 'gstCode' && cell.value != null) {
             
                return `${cell.value}` + "%";
              }
              if (cell.column.getColId() === 'lastModified' && cell.value != null) {
                return (cell.value as String).split(' ')[0];
              }
              return cell.value;
        },
        sheetName: "Managed Stock"
    }
}

// Export settings for Excel export
export function getManagedStockCoverPage(filterDetails: string): ExcelExportParams {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };
    const formattedDate = now.toLocaleString('en-US', options);
    return { 
      prependContent: [
          {
            cells: [
              { data: { type: "String", value: "Details" }, styleId: "coverPageMainHeader" }
            ]
          },
          {
            cells: [
              { data: { type: "String", value: "Name:" }, styleId: "coverPageHeading" },
              { data: { type: "String", value: "Managed Stock" } }
            ]
          },         
          {
            cells: [
              { data: { type: "String", value: "Filter Details:" } , styleId: "coverPageHeading"},
              { data: { type: "String", value: filterDetails}, styleId: "wrapText" }
            ]
          },
          {
            cells: [
              { data: { type: "String", value: "Date Generated:" }, styleId: "coverPageHeading" },
              { data: { type: "String", value: formattedDate  } }
            ]
          }
        ],       
        processHeaderCallback: () => "",
        processCellCallback: () => "",
        sheetName: "Details",
        columnWidth: 400
    }!;
  }